<template>
    <div ref="wysiwygRootRef" class="wysiwyg-editor_root">
        <VJoyLabel v-if="label" :size="labelSize" data-testid="wysiwyg-editor-label" :required="props.required" :optional-label="optionalLabel">
            {{ label }}
        </VJoyLabel>
        <div class="wysiwyg-editor" data-testid="wysiwyg-editor-wrapper" :class="{invalid: isInvalid, focus: isFocused, disabled: disabled}">
            <input type="hidden" :name="inputName" :value="inputValue" />
            <div v-if="!disabled" class="wysiwyg-editor__header">
                <div class="wysiwyg-editor__action-bar">
                    <button
                        v-for="action in actions"
                        :key="action.dataTestId"
                        type="button"
                        class="wysiwyg-editor__action"
                        :class="{'wysiwyg-editor__action--active': action.isActiveState.value}"
                        :disabled="action.isDisabledState && action.isDisabledState.value"
                        :title="action.title"
                        :aria-label="action.title"
                        :data-testid="dataTestId + '-' + action.dataTestId"
                        @click="doAction(action)"
                    >
                        <b v-if="action.iconText">{{ action.iconText }}</b>
                        <VJoyIcon v-if="action.iconJoy" :name="action.iconJoy" size="xsmall" class="wysiwyg-editor__action-icon" />
                    </button>
                </div>
                <div v-if="hasToolbarSlot" class="wysiwyg-editor__toolbar">
                    <slot name="toolbar"></slot>
                </div>
            </div>
            <div
                ref="divContentEditable"
                :contenteditable="!disabled"
                class="wysiwyg-editor__content"
                :class="{withToolbar: hasToolbarSlot, disabled: disabled}"
                :data-testid="dataTestId"
                :data-placeholder="placeholder"
                data-joy-form-field
                @input="onInput"
                @cut="cutOrCopy"
                @copy="cutOrCopy"
                @paste="preventPastingHtml"
                @mouseup="updateActionsStates"
                @keyup="updateActionsStates"
                @blur="onBlur"
                @focus="isFocused = true"
                v-html="initialHtmlContentValue"
            ></div>
            <div v-if="hasFooterSlot" class="wysiwyg-editor__footer" data-testid="wysiwyg-editor-footer">
                <slot name="footer"></slot>
            </div>
        </div>
        <div v-if="minlength || maxlength" class="wysiwyg-editor__helpers">
            <p
                v-if="maxlength && isValueOverMaxlength"
                data-testid="wysiwyg-editor-min-length-validation"
                :class="['wysiwyg-editor__helpers-min', {'wysiwyg-editor__helpers-min-invalid': isMaxlengthInvalid}]"
            >
                <VJoyIcon
                    :name="isMaxlengthInvalid ? 'warning-triangle' : 'info-circle'"
                    :color="isMaxlengthInvalid ? 'error' : 'neutral'"
                    size="xxsmall"
                />
                {{ maxlengthLabel || `${maxlength} characters maximum` }}
            </p>
            <p
                v-else-if="minlength"
                data-testid="wysiwyg-editor-min-length-validation"
                v-bind="isMinlengthInvalid ? {'data-joy-form-error': ''} : {}"
                :class="['wysiwyg-editor__helpers-min', {'wysiwyg-editor__helpers-min-invalid': isMinlengthInvalid}]"
            >
                <template v-if="isValueUnderMinlength">
                    <VJoyIcon
                        :name="isMinlengthInvalid ? 'warning-triangle' : 'info-circle'"
                        :color="isMinlengthInvalid ? 'error' : 'neutral'"
                        size="xxsmall"
                    />
                    {{ minlengthLabel || `${minlength} characters minimum` }}
                </template>
                <template v-else>
                    <VJoyIcon :name="validIcon" :color="validIconColor" size="xxsmall" />
                    {{ validLabel ?? (minlengthLabel || `${minlength} characters minimum`) }}
                </template>
            </p>
            <p v-if="isValueUnderMinlength && minCounter">
                {{ textLength + '/' + (maxlength ?? minlength) }}
            </p>
            <p
                v-if="maxlength && !isValueUnderMinlength"
                :class="['wysiwyg-editor__helpers-count', {'wysiwyg-editor__helpers-count-invalid': isMaxlengthInvalid}]"
            >
                {{ textLength + '/' + maxlength }}
            </p>
        </div>
    </div>
</template>
<script setup lang="ts">
    import {computed, onMounted, ref, useTemplateRef} from 'vue';
    import {useTranslation, watch} from '#imports';
    import {type TJoyIconsColors, type TJoyIconsNames, type TJoyLabelSizes, VJoyIcon, VJoyLabel} from '@maltjoy/core-vue';
    import {useMarkdownConverter} from '../../../composables/useMarkdownConverter';
    import type {Action, ActionName} from '../../../types/wysiwyg';

    interface Props {
        /**
         * The input name
         */
        inputName?: string;
        /**
         * The custom data-testid
         */
        dataTestId?: string;
        /**
         * The label of the field
         */
        label?: string;
        /**
         * The optional label of the field
         */
        optionalLabel?: string;
        /**
         * The label size
         */
        labelSize?: TJoyLabelSizes;
        /**
         * The placeholder shown when there is no text
         */
        placeholder?: string;
        /**
         * The model value
         */
        modelValue?: string;
        /**
         * Max length of the field
         */
        maxlength?: number;
        /**
         * Min length if the field
         */
        minlength?: number;
        /**
         * Min length message
         */
        minlengthLabel?: string;
        /**
         * Max length message
         */
        maxlengthLabel?: string;
        /**
         * Min message with counter
         */
        minCounter?: boolean;
        /**
         * Label when content is valid
         */
        validLabel?: string;
        /**
         * Icon when content is valid
         */
        validIcon?: TJoyIconsNames;
        /**
         * Icon color when content is valid
         */
        validIconColor?: TJoyIconsColors;
        /**
         * Add * to label or not
         */
        required?: boolean;
        /**
         * Min message with counter
         */
        invalid?: boolean;
        /**
         * If true, validation errors only show when both length condition AND invalid prop are true
         */
        strictValidation?: boolean;
        /**
         * Autofocus the content editable block when mounting
         */
        autofocus?: boolean;
        /**
         * Available actions
         */
        availableActions?: ActionName[];
        /**
         * Disable the editor
         */
        disabled?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        inputName: 'wysiwyg-editor-input',
        dataTestId: 'wysiwyg-editor',
        labelSize: 'medium',
        placeholder: '',
        modelValue: '',
        minlengthLabel: '',
        maxlengthLabel: '',
        minCounter: false,
        validIcon: 'check',
        validIconColor: 'neutral',
        required: false,
        invalid: undefined,
        strictValidation: false,
        autofocus: false,
        availableActions: () => ['heading', 'bold', 'list'],
        disabled: false,
    });

    const emit = defineEmits<{
        /**
         * Allows the use of v-model on the component
         *
         * @property {string} Actual model value
         */
        'update:modelValue': [modelValue: string];
        blur: [];
    }>();

    const slots = defineSlots<{
        toolbar: () => any;
        footer: () => any;
    }>();

    const {t} = useTranslation();
    const {htmlToMd, mdToHtml} = useMarkdownConverter();

    const initialHtmlContentValue = mdToHtml(props.modelValue);

    const divContentEditable = useTemplateRef('divContentEditable');
    const wysiwygRootRef = useTemplateRef('wysiwygRootRef');
    const inputValue = ref(props.modelValue);
    const textLength = ref(props.modelValue.length);
    const isFocused = ref(false);

    const isValueOverMaxlength = computed(() => props.maxlength && props.maxlength < textLength.value);
    const isValueUnderMinlength = computed(() => props.minlength && props.minlength > textLength.value);
    const isInvalid = computed(() => (props.invalid === undefined ? isValueOverMaxlength.value || isValueUnderMinlength.value : props.invalid));

    const isMaxlengthInvalid = computed(() =>
        props.strictValidation ? isValueOverMaxlength.value && props.invalid : isValueOverMaxlength.value || props.invalid,
    );
    const isMinlengthInvalid = computed(() =>
        props.strictValidation ? isValueUnderMinlength.value && props.invalid : isValueUnderMinlength.value || props.invalid,
    );

    const hasToolbarSlot = computed(() => !!slots.toolbar);
    const hasFooterSlot = computed(() => !!slots.footer);

    onMounted(() => {
        if (divContentEditable.value) {
            if (props.autofocus) {
                divContentEditable.value.focus();
                if (props.modelValue.length > 0) {
                    moveCursorToLastCharacter();
                }
            }
        }
    });

    watch(
        () => props.modelValue,
        (newValue) => {
            if (newValue !== inputValue.value) {
                updateHtmlContent();
            }
        },
    );

    function updateHtmlContent() {
        if (divContentEditable.value) {
            divContentEditable.value.innerHTML = mdToHtml(props.modelValue);
            wrapTextIntoBlock(divContentEditable.value);
            unwrapNestedDivs(divContentEditable.value);
            if (divContentEditable.value) {
                // Handle edge cases if the pages rerender, it can't find the divContentEditable after the "mdToHtml"
                inputValue.value = htmlToMd(divContentEditable.value.innerHTML);
            }
            /**
             * In backend (profile-backend), the validation of max length counts the chars in the
             * payload. With the introduction of wysiwyg, now the length of payload
             * becomes: text length + markdown chars. This requires an update in the
             * backend validations and the payload i.e., frontend need to send: 1. plain
             * description to count the chars for validation and, 2. description
             * with markdown to save in the DB.
             *
             * This requires to display inputValue.value.length as a char count in
             * wysiwyg max length validation instead of innerText to avoid
             * misinformation to the user because now the actual length is text
             * length + markdown chars and not only the text length.
             *
             * Once the above mentioned backend changes are done, we can then
             * use innerText to display the actual char count (without markdown
             * chars).
             */
            textLength.value = inputValue.value.length;
        }
    }

    function onInput(event: Event) {
        wrapTextIntoBlock(event.target as HTMLElement);
        unwrapNestedDivs(event.target as HTMLElement);
        emitModelValue();
        updateActionsStates();
    }

    function onBlur() {
        emit('blur');
        isFocused.value = false;
    }

    function onFocus() {
        isFocused.value = true;
    }

    function emitModelValue() {
        if (divContentEditable.value) {
            if (divContentEditable.value.innerHTML === '<br>') {
                divContentEditable.value.innerHTML = '';
            }
            inputValue.value = htmlToMd(divContentEditable.value.innerHTML);
            textLength.value = inputValue.value.length;
            emit('update:modelValue', inputValue.value);
        }
    }

    function doAction(action: {execute: () => void}) {
        const selection = window.getSelection();

        if (selection?.anchorNode?.parentNode && wysiwygRootRef.value?.contains(selection.anchorNode.parentNode)) {
            action.execute();
            divContentEditable.value?.focus();
            updateActionsStates();
        }
    }

    const allActions: Record<ActionName, Action> = {
        heading: {
            iconText: 'H',
            title: t('wysiwyg.action.title'),
            dataTestId: 'heading',
            isActiveState: ref(false),
            isActive: () => document.queryCommandValue('formatBlock') === 'h1',
            isDisabledState: ref(false),
            isDisabled: () => document.queryCommandState('insertUnorderedList'),
            execute: () => {
                if (document.queryCommandState('insertUnorderedList')) {
                    return;
                }
                if (document.queryCommandValue('formatBlock') === 'h1') {
                    document.execCommand('formatBlock', false, `<div>`);
                } else {
                    document.execCommand('formatBlock', false, `<h1>`);
                }
            },
        },
        bold: {
            iconText: 'B',
            title: t('wysiwyg.action.bold'),
            dataTestId: 'bold',
            isActiveState: ref(false),
            isActive: () => document.queryCommandState('bold') && document.queryCommandValue('formatBlock') !== 'h1',
            isDisabledState: ref(false),
            isDisabled: () => document.queryCommandValue('formatBlock') === 'h1',
            execute: () => document.execCommand('bold'),
        },
        list: {
            iconJoy: 'list',
            title: t('wysiwyg.action.list'),
            dataTestId: 'list',
            isActiveState: ref(false),
            isActive: () => document.queryCommandState('insertUnorderedList'),
            isDisabledState: ref(false),
            isDisabled: () => document.queryCommandValue('formatBlock') === 'h1',
            execute: () => {
                if (document.queryCommandValue('formatBlock') === 'h1') {
                    return;
                }
                document.execCommand('insertUnorderedList');
                removeAllInlineStyle();
            },
        },
    };

    const actions = computed(() => props.availableActions.map((action) => allActions[action]));

    function updateActionsStates() {
        actions.value.forEach((action) => {
            action.isActiveState.value = action.isActive();
            if (action.isDisabled && action.isDisabledState) {
                action.isDisabledState.value = action.isDisabled();
            }
        });
    }

    function removeAllInlineStyle() {
        if (divContentEditable.value) {
            divContentEditable.value.querySelectorAll('*').forEach((el) => el.removeAttribute('style'));
        }
    }

    function wrapTextIntoBlock(parentNode: Node | null) {
        parentNode?.childNodes.forEach((node) => {
            if (node && (node.nodeType === Node.TEXT_NODE || node.nodeName === 'B')) {
                const isFirstNode = node === parentNode?.childNodes[0];
                const div = document.createElement('div');
                node.after(div);
                div.appendChild(node);
                if (isFirstNode) {
                    moveCursorToOneCharacterToTheRight(parentNode);
                }
            }
        });
    }

    function moveCursorToOneCharacterToTheRight(parentNode: Node) {
        const range = document.createRange();
        const sel = window.getSelection()!;
        range.setStart(parentNode.childNodes[0]!, 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    function moveCursorToLastCharacter() {
        const range = document.createRange();
        const sel = window.getSelection()!;
        range.selectNodeContents(divContentEditable.value!);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    function unwrapNestedDivs(parentNode: Node | null) {
        if (!parentNode) {
            return;
        }
        Array.from(parentNode.childNodes).forEach((node) => {
            unwrapNestedDivs(node);
            if (!(node instanceof HTMLDivElement)) {
                return;
            }
            const onlyContainsDiv = Array.from(node.childNodes).every((node) => node instanceof HTMLDivElement);
            if (onlyContainsDiv) {
                node.outerHTML = node.innerHTML;
            }
        });
    }

    function cutOrCopy(e: ClipboardEvent) {
        e.preventDefault();
        if (!e.clipboardData) {
            return;
        }
        const selection: Selection | null = document.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range: Range = selection.getRangeAt(0);
            const fragment: DocumentFragment = range.cloneContents();
            const div: HTMLDivElement = document.createElement('div');
            div.appendChild(fragment);
            const html: string = div.innerHTML;

            const mdContent: string = htmlToMd(html);
            e.clipboardData.setData('text/plain', mdContent);

            if (e.type === 'cut') {
                selection.deleteFromDocument();
                emitModelValue();
            }
        }
    }

    function preventPastingHtml(e: ClipboardEvent) {
        e.preventDefault();
        if (!e.clipboardData) {
            return;
        }
        const pastedText = e.clipboardData.getData('text/plain');
        const htmlPastedText = mdToHtml(pastedText);
        const htmlElementPastedText = stringToHTMLElement(htmlPastedText);
        wrapTextIntoBlock(htmlElementPastedText);
        unwrapNestedDivs(htmlElementPastedText);
        document.execCommand('insertHTML', false, htmlElementPastedText.innerHTML);
    }

    function stringToHTMLElement(str: string) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(str, 'text/html');

        return doc.body;
    }
</script>

<style lang="scss" scoped>
    .wysiwyg-editor {
        position: relative;
        border-radius: var(--joy-core-radius-3);
        border: var(--joy-form-field-border-width) solid var(--joy-color-neutral-30);

        &.invalid {
            border-color: var(--joy-color-error-50);

            &:hover,
            &.focus {
                border-color: var(--joy-color-primary-90);
            }
        }

        &.focus {
            border-color: var(--joy-color-border-hover);
        }

        &.disabled {
            background-color: var(--joy-color-neutral-20);
            opacity: 0.6;
            cursor: not-allowed;
        }

        &__header {
            display: flex;
            flex-wrap: wrap-reverse;
            justify-content: space-between;
            background-color: var(--joy-color-neutral-20);
            border-radius: var(--joy-core-radius-3) var(--joy-core-radius-3) 0 0;
        }

        &__action-bar {
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            flex-grow: 100;
            padding: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
            gap: var(--joy-core-spacing-2);
            border-bottom: 1px solid var(--joy-color-neutral-30);
        }

        &__toolbar {
            flex-grow: 1;
            border-bottom: 1px solid var(--joy-color-neutral-30);
        }

        &__action {
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: var(--joy-core-radius-2);
            border: 1px solid transparent;
            height: 28px;
            width: 28px;
            transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            color: var(--joy-color-neutral-40);
            cursor: pointer;

            $p: &;

            &:not(#{p}--active):not(:disabled):hover {
                background-color: var(--joy-color-neutral-30);
            }

            &--active {
                background-color: var(--joy-color-neutral-30);
            }

            &:disabled {
                cursor: not-allowed;
                opacity: 0.4;
            }

            &:focus {
                border: 2px solid var(--joy-form-field-focus-color);
                box-shadow: inset 0 0 0 2px var(--joy-color-neutral-0);
            }
        }

        &__action-icon {
            color: var(--joy-color-neutral-40);
        }

        &__content {
            width: 100%;
            color: var(--joy-color-neutral-60);
            max-width: 100%;

            // anywhere (not break-word): shrinks min-content so long words wrap instead of widening the editor
            overflow-wrap: anywhere;
            border-radius: var(--joy-core-radius-3);
            border-top-left-radius: 0;
            padding: var(--joy-core-spacing-4);
            transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            min-height: 117px;
            font-weight: var(--joy-font-weight-normal); // Mandatory for firefox to consider text not bold by default

            &:focus {
                border-color: var(--joy-color-border-focus);
                outline: none;
            }

            &:hover {
                border-color: var(--joy-color-border-hover);
            }

            &.disabled {
                cursor: not-allowed;
                user-select: none;
                border-top-left-radius: var(--joy-core-radius-3);
            }

            &:empty:not(:focus)::before {
                content: attr(data-placeholder);
                white-space: pre-wrap;
                color: var(--joy-color-neutral-40);
                cursor: text;
                position: relative;
                z-index: 0;
                pointer-events: none;
            }

            :deep(h1) {
                font-size: var(--joy-font-size-primary-600);
                margin: 0 0 var(--joy-core-spacing-4) 0;
            }

            :deep(ul) {
                padding-inline-start: 32px;
            }
        }

        &__footer {
            position: relative;
            padding: 0 var(--joy-core-spacing-4) var(--joy-core-spacing-4);

            &::before {
                content: '';
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                height: var(--joy-core-spacing-6);
                background: linear-gradient(to top, var(--wysiwyg-editor-surface, transparent), transparent);
                pointer-events: none;
            }
        }

        &__helpers {
            margin-top: var(--joy-core-spacing-2);
            margin-bottom: var(--joy-core-spacing-6);
            display: flex;
            align-items: center;
            justify-content: space-between;

            p {
                display: flex;
                align-items: center;
                font-size: var(--joy-font-size-primary-300);
                margin: 0;
                padding: 0;
            }

            &-min {
                .joy-icon {
                    margin-right: var(--joy-core-spacing-2);
                }
            }

            &-min-invalid {
                color: var(--joy-color-error-50);
            }

            &-count-invalid {
                color: var(--joy-color-error-50);
            }
        }
    }
</style>
